/**
 * Zandvoort — interactivite de la page produit dediee.
 *
 * Trois responsabilites, aucune dependance tierce :
 *   1. la carte du casque : allumer le point du texte en cours de lecture
 *   2. les revelations au defilement, quand le navigateur ignore
 *      animation-timeline: view()
 *   3. la barre d'achat fixe
 *
 * La page fonctionne sans ce module : les quatre textes de la carte restent
 * pleinement lisibles et leurs pastilles sont de simples ancres, la barre
 * fixe reste masquee, et le formulaire d'achat est un formulaire HTML natif.
 */

import { prefersReducedMotion } from '@theme/utilities';

/* -------------------------------------------------------------------------
 * 1. Points chauds
 * ---------------------------------------------------------------------- */

class ZvHotspots extends HTMLElement {
  connectedCallback() {
    this.points = [...this.querySelectorAll('[data-zv-hotspot]')];
    this.panneaux = [...this.querySelectorAll('[data-zv-panel]')];
    if (!this.points.length || !this.panneaux.length) return;

    // Signale au CSS que le module a pris la main : les panneaux inactifs
    // s'estompent. Sans cet attribut, les quatre restent pleinement lisibles.
    this.dataset.zvReady = '';

    this.actif = null;

    // Bande de lecture au tiers central de l'ecran : le panneau qui y entre
    // devient actif. Un ecouteur de defilement ferait le meme travail en
    // recalculant a chaque image.
    this.observateur = new IntersectionObserver(
      (entrees) => {
        entrees.forEach((entree) => {
          if (entree.isIntersecting) this.activer(entree.target.dataset.zvIndex);
        });
      },
      { rootMargin: '-35% 0px -35% 0px', threshold: 0 }
    );
    this.panneaux.forEach((p) => this.observateur.observe(p));

    // Etat de depart, avant tout defilement.
    this.activer(this.panneaux[0].dataset.zvIndex);
  }

  disconnectedCallback() {
    this.observateur?.disconnect();
  }

  activer(index) {
    if (!index || index === this.actif) return;
    this.actif = index;

    this.panneaux.forEach((p) => {
      p.classList.toggle('is-active', p.dataset.zvIndex === index);
    });
    this.points.forEach((point) => {
      const courant = point.dataset.zvIndex === index;
      point.setAttribute('aria-current', courant ? 'true' : 'false');
    });
  }
}

if (!customElements.get('zv-hotspots')) {
  customElements.define('zv-hotspots', ZvHotspots);
}

/* -------------------------------------------------------------------------
 * 1 bis. Trainee de detail sous le pointeur
 * ---------------------------------------------------------------------- */

/**
 * Meme mecanique que la reference : un pool fixe de vignettes montees une fois,
 * garees a opacite zero. On accumule la distance parcourue par le pointeur, et
 * chaque fois qu'elle passe un seuil, la vignette suivante est teleportee sous
 * le curseur et jouee. Rien n'est cree ni detruit pendant le mouvement — c'est
 * ce qui empeche un balayage rapide d'etrangler le DOM.
 *
 * Une difference avec la reference : les vignettes sont les lettres de
 * ZANDVOORT et se suivent dans l'ordre, donc le pointeur ecrit le mot. Deux
 * consequences sur les reglages :
 *
 *   - le pas est plus court et la duree de vie plus longue qu'avec des images
 *     independantes, sinon les premieres lettres ont disparu avant la derniere
 *     et le mot ne se lit jamais en entier ;
 *   - une pause du pointeur ramene a la premiere lettre, pour que chaque
 *     balayage reparte de Z plutot que de finir un mot commence il y a
 *     trente secondes.
 *
 * Le pool est plus grand que la liste d'images : a vitesse elevee une vignette
 * est relancee avant que la precedente ait fini de s'effacer, et reutiliser un
 * emplacement encore anime le fait sauter.
 */
const TRAIL_SLOTS = 14;

/** Fraction de la largeur d'ecran que le pointeur doit couvrir entre deux lettres. */
const TRAIL_STEP = 0.055;

/** Duree de vie d'une lettre. Assez longue pour que les neuf coexistent. */
const TRAIL_LIFE = 2400;

/** Au-dela de cette pause, le mot recommence a Z. */
const TRAIL_RESET = 900;

function initTrainee() {
  const racine = document.querySelector('[data-zv-trail]');
  if (!racine) return;

  // Sans pointeur fin, l'effet n'a pas de sens ; sous mouvement reduit, il est
  // exactement ce qu'on ne veut pas.
  if (!matchMedia('(hover: hover) and (pointer: fine)').matches) return;
  if (prefersReducedMotion()) return;

  const images = (racine.dataset.frames || '').split(',').map((s) => s.trim()).filter(Boolean);
  if (!images.length) return;

  const slots = [];
  let monte = false;

  const monterPool = () => {
    if (monte) return;
    monte = true;
    for (let i = 0; i < TRAIL_SLOTS; i += 1) {
      const img = document.createElement('img');
      img.src = images[i % images.length];
      img.alt = '';
      img.draggable = false;
      img.decoding = 'async';
      racine.appendChild(img);
      slots.push(img);
    }
  };

  // Monte apres le chargement, quand le navigateur n'a plus rien d'urgent :
  // ces vignettes ne doivent jamais concurrencer l'image du hero.
  const planifier = window.requestIdleCallback || ((fn) => setTimeout(fn, 800));
  if (document.readyState === 'complete') planifier(monterPool);
  else window.addEventListener('load', () => planifier(monterPool), { once: true });

  let slot = 0;
  let lettre = 0;
  let lastX = 0;
  let lastY = 0;
  let lastT = 0;
  let amorce = false;

  const poser = (x, y) => {
    const el = slots[slot % TRAIL_SLOTS];
    slot += 1;
    if (!el) return;

    const rang = lettre % images.length;
    el.src = images[rang];
    lettre += 1;

    // Inclinaison alternee et deterministe, pas aleatoire : le mot doit rester
    // lisible comme une ligne de texte collee de travers. L'alternance regle
    // aussi le cas des deux O, qui sont le meme fichier — penches en sens
    // contraire, ils ne se lisent pas comme un doublon.
    const angle = (rang % 2 ? 1 : -1) * (4 + (rang % 3) * 2.5);
    const base = `translate(-50%, -50%) rotate(${angle}deg)`;

    el.style.left = `${x}px`;
    el.style.top = `${y}px`;

    el.getAnimations().forEach((a) => a.cancel());
    el.animate(
      [
        { opacity: 0, transform: `${base} scale(0.72)` },
        { opacity: 1, transform: `${base} scale(1)`, offset: 0.12 },
        { opacity: 1, transform: `${base} scale(1)`, offset: 0.72 },
        { opacity: 0, transform: `${base} scale(1.04)` },
      ],
      { duration: TRAIL_LIFE, easing: 'cubic-bezier(0.16, 1, 0.3, 1)', fill: 'forwards' }
    );
  };

  window.addEventListener(
    'pointermove',
    (event) => {
      if (!amorce) {
        amorce = true;
        lastX = event.clientX;
        lastY = event.clientY;
        lastT = event.timeStamp;
        return;
      }

      // Pointeur repose : le prochain balayage repart de la premiere lettre.
      if (event.timeStamp - lastT > TRAIL_RESET) lettre = 0;
      lastT = event.timeStamp;

      // Hors des zones autorisees, on continue de suivre le pointeur sans rien
      // poser : sinon le premier mouvement au retour part d'une distance
      // perimee et declenche aussitot.
      const dansZone = event.target instanceof Element && event.target.closest('[data-zv-trail-zone]');
      if (!dansZone) {
        lastX = event.clientX;
        lastY = event.clientY;
        return;
      }

      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      if (Math.hypot(dx, dy) < window.innerWidth * TRAIL_STEP) return;

      lastX = event.clientX;
      lastY = event.clientY;
      monterPool();
      poser(event.clientX, event.clientY);
    },
    { passive: true }
  );
}

/* -------------------------------------------------------------------------
 * 2. Revelations au defilement
 * ---------------------------------------------------------------------- */

function initRevelations() {
  const cibles = document.querySelectorAll('.zv-reveal');
  if (!cibles.length) return;

  // Le navigateur sait le faire nativement : le CSS s'en charge, on sort.
  if (CSS.supports('animation-timeline: view()')) return;

  // Mouvement reduit : tout est deja visible, on ne touche a rien.
  if (prefersReducedMotion()) {
    cibles.forEach((el) => el.classList.add('zv-revealed'));
    return;
  }

  const observateur = new IntersectionObserver(
    (entrees) => {
      entrees.forEach((entree) => {
        if (!entree.isIntersecting) return;
        entree.target.classList.add('zv-revealed');
        observateur.unobserve(entree.target);
      });
    },
    { rootMargin: '0px 0px -10% 0px' }
  );

  cibles.forEach((el) => {
    el.classList.add('zv-reveal--js');
    observateur.observe(el);
  });
}

/* -------------------------------------------------------------------------
 * 3. Barre d'achat fixe
 * ---------------------------------------------------------------------- */

function initBarreFixe() {
  const barre = document.querySelector('[data-zv-sticky]');
  const hero = document.querySelector('[data-zv-hero]');
  const buy = document.querySelector('[data-zv-buy]');
  if (!barre || !hero) return;

  const prix = barre.querySelector('[data-zv-sticky-price]');
  let heroVisible = true;
  let buyVisible = false;

  const rafraichir = () => {
    // Masquee tant qu'on est dans le hero, et masquee a nouveau quand le
    // bloc d'achat est a l'ecran : elle n'aurait plus rien a apporter.
    const doitAfficher = !heroVisible && !buyVisible;
    barre.toggleAttribute('hidden', !doitAfficher);
  };

  // IntersectionObserver plutot qu'un ecouteur de defilement : pas de
  // recalcul a chaque image.
  new IntersectionObserver(
    ([entree]) => {
      heroVisible = entree.isIntersecting;
      rafraichir();
    },
    { threshold: 0 }
  ).observe(hero);

  if (buy) {
    new IntersectionObserver(
      ([entree]) => {
        buyVisible = entree.isIntersecting;
        rafraichir();
      },
      { threshold: 0 }
    ).observe(buy);
  }

  // Le prix de la barre suit l'echelle choisie.
  document.querySelectorAll('[data-zv-variant]').forEach((radio) => {
    radio.addEventListener('change', () => {
      if (radio.checked && prix && radio.dataset.price) {
        prix.textContent = radio.dataset.price;
      }
    });
  });
}

/* ---------------------------------------------------------------------- */

function init() {
  initRevelations();
  initBarreFixe();
  initTrainee();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
